/**
 * PianoKeyboard Component
 * -----------------------
 * Studio Console Design: Dark recessed panel, amber glow on active keys, cyan highlight
 * Interactive virtual keyboard showing MIDI input and allowing mouse/touch input.
 */

import { useCallback, useMemo } from "react";
import { motion } from "framer-motion";

interface PianoKeyboardProps {
  activeNotes: Set<number>;
  highlightNote?: number | null;
  onNoteClick?: (midi: number) => void;
  onNoteDown?: (midi: number) => void;
  onNoteUp?: (midi: number) => void;
  startNote?: number;
  endNote?: number;
}

const BLACK_KEY_OFFSETS = new Set([1, 3, 6, 8, 10]);
const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

export default function PianoKeyboard({
  activeNotes,
  highlightNote,
  onNoteClick,
  onNoteDown,
  onNoteUp,
  startNote = 48,
  endNote = 84,
}: PianoKeyboardProps) {
  const isBlack = (midi: number) => BLACK_KEY_OFFSETS.has(midi % 12);

  const whiteKeys = useMemo(() => {
    const keys: number[] = [];
    for (let m = startNote; m <= endNote; m++) {
      if (!isBlack(m)) keys.push(m);
    }
    return keys;
  }, [startNote, endNote]);

  const blackKeys = useMemo(() => {
    const keys: number[] = [];
    for (let m = startNote; m <= endNote; m++) {
      if (isBlack(m)) keys.push(m);
    }
    return keys;
  }, [startNote, endNote]);

  const getWhiteKeyColor = useCallback(
    (midi: number) => {
      const isActive = activeNotes.has(midi);
      const isHighlight = highlightNote === midi;
      if (isActive) return "#ffd54f";
      if (isHighlight) return "#00d4ff";
      return "#d0d0e0";
    },
    [activeNotes, highlightNote]
  );

  const getBlackKeyColor = useCallback(
    (midi: number) => {
      const isActive = activeNotes.has(midi);
      const isHighlight = highlightNote === midi;
      if (isActive) return "#d4a017";
      if (isHighlight) return "#1a1a2e";
      return "#1a1a2e";
    },
    [activeNotes, highlightNote]
  );

  // Position black keys relative to white keys
  const getBlackKeyLeft = useCallback(
    (midi: number) => {
      // Find the white key immediately before this black key
      const prevWhite = midi - 1;
      const whiteIdx = whiteKeys.indexOf(prevWhite);
      if (whiteIdx < 0) return -100; // off screen
      const whiteKeyWidth = 100 / whiteKeys.length;
      return (whiteIdx + 0.65) * whiteKeyWidth;
    },
    [whiteKeys]
  );

  const whiteKeyWidth = 100 / whiteKeys.length;

  return (
    <div
      className="relative select-none"
      style={{
        height: 90,
        background: "oklch(0.14 0.015 270)",
        border: "1px solid oklch(0.22 0.012 270)",
        borderRadius: "0 0 6px 6px",
        boxShadow: "inset 0 2px 6px oklch(0 0 0 / 30%)",
        overflow: "hidden",
      }}
    >
      {/* White keys */}
      <div className="relative h-full flex">
        {whiteKeys.map((midi) => {
          const isActive = activeNotes.has(midi);
          const isHighlight = highlightNote === midi;
          const octave = Math.floor(midi / 12) - 1;
          const noteName = NOTE_NAMES[midi % 12];
          const showLabel = midi % 12 === 0; // Show C labels

          return (
            <motion.div
              key={midi}
              className="relative flex items-end justify-center pb-1"
              style={{
                flex: `0 0 ${whiteKeyWidth}%`,
                backgroundColor: getWhiteKeyColor(midi),
                borderRight: "1px solid oklch(0.28 0.010 270)",
                borderRadius: "0 0 3px 3px",
                transition: "background-color 75ms ease",
              }}
              whileTap={{ scale: 0.98, y: 1 }}
              animate={isActive ? { y: 1 } : { y: 0 }}
              onPointerDown={(event) => {
                event.preventDefault();
                if (onNoteDown) onNoteDown(midi);
                else onNoteClick?.(midi);
              }}
              onPointerUp={(event) => {
                event.preventDefault();
                onNoteUp?.(midi);
              }}
              onPointerCancel={() => {
                onNoteUp?.(midi);
              }}
            >
              {/* Subtle gradient for 3D effect */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: isHighlight
                    ? "linear-gradient(180deg, oklch(0.82 0.14 200 / 30%) 0%, transparent 40%)"
                    : isActive
                    ? "linear-gradient(180deg, oklch(0.90 0.17 75 / 30%) 0%, transparent 40%)"
                    : "linear-gradient(180deg, oklch(1 0 0 / 40%) 0%, oklch(1 0 0 / 10%) 100%)",
                  borderRadius: "0 0 3px 3px",
                }}
              />
              {showLabel && (
                <span
                  className="text-[8px] font-mono relative z-10"
                  style={{ color: isHighlight ? "#004d5a" : "oklch(0.45 0.01 270)" }}
                >
                  {noteName}{octave}
                </span>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Black keys */}
      {blackKeys.map((midi) => {
        const left = getBlackKeyLeft(midi);
        if (left < 0) return null;
        const isActive = activeNotes.has(midi);
        const isHighlight = highlightNote === midi;

        return (
          <motion.div
            key={midi}
            className="absolute top-0 z-10"
            style={{
              left: `${left}%`,
              width: `${whiteKeyWidth * 0.58}%`,
              height: "58%",
              backgroundColor: getBlackKeyColor(midi),
              borderRadius: "0 0 3px 3px",
              boxShadow: isActive || isHighlight
                ? isHighlight
                  ? "0 0 10px oklch(0.82 0.14 200 / 50%)"
                  : "0 0 8px oklch(0.82 0.17 75 / 40%)"
                : "0 2px 4px oklch(0 0 0 / 60%), inset 0 -1px 0 oklch(0.25 0.01 270)",
              border: "1px solid oklch(0.10 0.015 270)",
              transition: "background-color 75ms ease",
            }}
            whileTap={{ scale: 0.97, y: 1 }}
            animate={isActive ? { y: 1 } : { y: 0 }}
            onPointerDown={(event) => {
              event.preventDefault();
              // Capture so the matching pointerup fires on this key even if
              // the pointer is dragged off it before release. May throw for
              // inactive pointers (e.g. synthetic events) — never block input.
              try {
                event.currentTarget.setPointerCapture(event.pointerId);
              } catch {}
              if (onNoteDown) onNoteDown(midi);
              else onNoteClick?.(midi);
            }}
            onPointerUp={(event) => {
              event.preventDefault();
              onNoteUp?.(midi);
            }}
            onPointerCancel={() => {
              onNoteUp?.(midi);
            }}
          >
            {/* Gradient for 3D effect */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: isHighlight
                  ? "linear-gradient(180deg, oklch(0.82 0.14 200 / 30%) 0%, transparent 50%)"
                  : isActive
                  ? "linear-gradient(180deg, oklch(0.82 0.17 75 / 20%) 0%, transparent 50%)"
                  : "linear-gradient(180deg, oklch(0.30 0.01 270) 0%, oklch(0.12 0.015 270) 100%)",
                borderRadius: "0 0 3px 3px",
              }}
            />
          </motion.div>
        );
      })}
    </div>
  );
}
